import {
  TextField as MuiTextField,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { useDesignTokens } from "../../../theme/useDesignTokens";

/**
 * Common Input Component
 * Provides consistent form field styling, validation, and error handling
 * Supports TextField, Select, Textarea, and custom types
 */
const FormInput = ({
  id,
  type = "text",
  label,
  name,
  value = "",
  onChange,
  onBlur,
  error = false,
  helperText = "",
  placeholder = "",
  fullWidth = true,
  size = "small",
  disabled = false,
  required = false,
  multiline = false,
  rows = 1,
  slotProps = {},
  onKeyDown,
  options = [], // For select
  sx = {},
  touched = "false",
  startAdornment = null,
  endAdornment = null,
  // showError = touched && error,
  ...props
}) => {
  const { colors, border } = useDesignTokens();
  const showError = (touched === "true" ? true : false) && error;

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
  };

  const mergedSlotProps = {
    ...slotProps,

    /** Native input element */
    htmlInput: {
      ...(slotProps?.htmlInput || {}),
      className: ["browser-default", slotProps?.htmlInput?.className]
        .filter(Boolean)
        .join(" "),
    },

    /** MUI InputBase (adornments live here) */
    input: {
      ...(slotProps?.input || {}),

      ...(startAdornment && {
        startAdornment: (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ),
      }),

      ...(endAdornment && {
        endAdornment: (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ),
      }),
    },
  };

  const commonSx = {
    "& .MuiInputBase-input": {
      boxSizing: "border-box !important",
      borderBottom: "none !important", // Explicitly kill Materialize border
      // outline: "none !important",
      boxShadow: "none !important", // Explicitly kill Materialize shadow
      margin: "0 !important",
      height:
        size === "small"
          ? multiline
            ? "auto !important"
            : "41px !important"
          : "auto !important",
      padding:
        size === "small" ? "8.5px 14px !important" : "16.5px 14px !important",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.primary.main + " !important",
      borderWidth: border?.twoPx + "!important",
    },
    "& .Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.error?.main + " !important",
      borderWidth: border?.twoPx + "!important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: colors?.primary?.main + " !important",
    },
    "& .MuiInputLabel-root.Mui-error": {
      color: colors?.error?.main + " !important",
    },
    ...sx, // Allow parent overrides
  };

  // TextField and Textarea
  if (
    type === "text" ||
    type === "number" ||
    type === "email" ||
    type === "password" ||
    type === "textarea"
  ) {
    return (
      <MuiTextField
        fullWidth={fullWidth}
        label={label}
        // id={name}
        id={id || name}
        name={name}
        type={type === "textarea" ? "text" : type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={showError}
        helperText={helperText}
        placeholder={placeholder}
        size={size}
        disabled={disabled}
        required={required}
        multiline={type === "textarea" || multiline}
        rows={type === "textarea" ? rows : multiline ? rows : undefined}
        onKeyDown={onKeyDown ? handleKeyDown : undefined}
        slotProps={mergedSlotProps}
        sx={commonSx}
        {...props}
      />
    );
  }

  // Select/Dropdown
  if (type === "select") {
    return (
      <FormControl
        fullWidth={fullWidth}
        size={size}
        error={showError}
        disabled={disabled}
      >
        {label && <InputLabel>{label}</InputLabel>}
        <MuiSelect
          // id={name}
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown ? handleKeyDown : undefined}
          label={label}
          disabled={disabled}
          slotProps={{
            input: {
              className: "browser-default",
            },
          }}
          sx={commonSx}
          {...props}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
        {(showError || helperText) && (
          <FormHelperText>{showError ? helperText : helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }

  return null;
};

export default FormInput;
